import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Megaphone, MapPin, Trophy, Plus, Trash2, Award, Download } from "lucide-react";
import AppShell, { useAppUser } from "@/components/AppShell";
import MuralAniversariantes from "@/components/MuralAniversariantes";
import BeltBar, { BELT_LABELS, degreeLabel } from "@/components/BeltBar";
import { authApi, formatApiError } from "@/lib/api";

const inputCls =
  "w-full bg-obsidian border border-white/10 focus:border-gold/60 rounded-xl px-4 py-3 text-sm text-[#F5F5F0] placeholder:text-zinc-600 outline-none transition-colors duration-200";

const CLASS_INFO = [
  { group: "Crianças (até 12 anos)", time: "19:00 — 20:30", testid: "student-class-kids" },
  { group: "Adultos", time: "20:30 — 22:00", testid: "student-class-adults" },
];

export default function StudentArea() {
  const { user } = useAppUser() || {};
  const [avisos, setAvisos] = useState(null);
  const [historico, setHistorico] = useState(null);
  const [conquistas, setConquistas] = useState(null);
  const [certificados, setCertificados] = useState(null);
  const [presenca, setPresenca] = useState(null);
  const [rank, setRank] = useState(null);
  const [downloading, setDownloading] = useState(null);
  const [conqForm, setConqForm] = useState({ title: "", competition: "", date: "" });
  const [savingConq, setSavingConq] = useState(false);
  const navigate = useNavigate();

  const refreshConquistas = () =>
    authApi().get("/conquistas/me").then((r) => setConquistas(r.data.achievements)).catch(() => setConquistas([]));

  useEffect(() => {
    if (!user) return;
    if ((user.roles || [user.role]).includes("admin")) {
      navigate("/admin");
      return;
    }
    authApi().get("/announcements").then((r) => setAvisos(r.data.announcements)).catch(() => setAvisos([]));
    authApi().get("/graduations/me").then((r) => setHistorico(r.data.graduations)).catch(() => setHistorico([]));
    authApi().get("/certificados/me").then((r) => setCertificados(r.data.certificados)).catch(() => setCertificados([]));
    authApi().get("/presenca/me").then((r) => setPresenca(r.data)).catch(() => setPresenca({ total: 0, aulas: [] }));
    authApi().get("/frequencia/meu-rank").then((r) => setRank(r.data)).catch(() => setRank({ disponivel: false }));
    refreshConquistas();
  }, [user, navigate]);

  const downloadCertificado = async (c) => {
    setDownloading(c.id);
    try {
      const r = await authApi().get(`/certificados/${c.id}/arquivo`);
      const a = document.createElement("a");
      a.href = r.data.data_base64;
      a.download = r.data.file_name;
      a.click();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setDownloading(null);
    }
  };

  const submitConquista = async (e) => {
    e.preventDefault();
    setSavingConq(true);
    try {
      await authApi().post("/conquistas", {
        title: conqForm.title,
        competition: conqForm.competition || null,
        date: conqForm.date || null,
      });
      toast.success("Conquista registrada. Parabéns!");
      setConqForm({ title: "", competition: "", date: "" });
      await refreshConquistas();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSavingConq(false);
    }
  };

  const removeConquista = async (c) => {
    if (!window.confirm(`Remover a conquista "${c.title}"?`)) return;
    try {
      await authApi().delete(`/conquistas/${c.id}`);
      toast.success("Conquista removida.");
      await refreshConquistas();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <AppShell active="minha-area">
      <h1 className="font-display text-2xl font-bold" data-testid="student-title">
        {user ? `Olá, ${user.name.split(" ")[0]}. Oss!` : "Área do Aluno"}
      </h1>
      <p className="font-mono text-xs tracking-[0.3em] text-gold mt-2" data-testid="student-user-info">SEU ESPAÇO NO PROJETO</p>

      <div className="mt-8">
        <MuralAniversariantes />
      </div>

      <div className="mt-8 grid md:grid-cols-3 gap-6">
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-[#1C1C23] border border-white/10 rounded-2xl p-8"
          data-testid="student-belt-card"
        >
          <p className="font-mono text-xs tracking-[0.3em] text-gold mb-5">MINHA GRADUAÇÃO</p>
          <BeltBar belt={user?.belt || "branca"} degree={user?.degree || 0} testid="student-belt-bar" />
          <p className="font-display text-xl font-bold mt-4" data-testid="student-belt-label">
            Faixa {BELT_LABELS[user?.belt || "branca"]} · {degreeLabel(user?.degree || 0)}
          </p>
          <p className="text-sm text-zinc-500 mt-2 leading-relaxed">
            As graduações são certificadas e validadas pelo Prof. Marciano Leme, faixa-preta ZR Team Sorocaba.
          </p>
        </motion.section>

        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.05 }}
          className="bg-[#1C1C23] border border-white/10 rounded-2xl p-8"
          data-testid="student-attendance"
        >
          <p className="font-mono text-xs tracking-[0.3em] text-gold mb-5">MINHA PRESENÇA</p>
          {presenca === null ? (
            <p className="text-sm text-zinc-500">Carregando...</p>
          ) : (
            <>
              <p className="font-display text-5xl font-bold text-gold" data-testid="student-attendance-total">
                {presenca.total}
              </p>
              <p className="text-sm text-zinc-400 mt-2" data-testid="student-attendance-label">
                {presenca.total === 1 ? "aula feita" : "aulas feitas"} no projeto
              </p>
              <p
                className={`font-mono text-[11px] tracking-wider mt-3 ${presenca.desde_graduacao >= presenca.minimo_graduacao ? "text-green-400" : "text-yellow-400"}`}
                data-testid="student-attendance-progress"
              >
                {presenca.desde_graduacao}/{presenca.minimo_graduacao} AULAS DESDE A ÚLT. GRADUAÇÃO
              </p>
              {presenca.desde_graduacao < presenca.minimo_graduacao ? (
                <p className="text-[11px] text-zinc-500 mt-1.5" data-testid="student-attendance-hint">
                  Faltam {presenca.minimo_graduacao - presenca.desde_graduacao} aula(s) para liberar sua próxima graduação.
                </p>
              ) : (
                <p className="text-[11px] text-green-500/80 mt-1.5" data-testid="student-attendance-hint">
                  Frequência suficiente para graduar. Oss!
                </p>
              )}
              {presenca.aulas.length > 0 && (
                <p className="font-mono text-[11px] text-zinc-600 mt-3" data-testid="student-attendance-last">
                  Última: {presenca.aulas[0].unit_name} · {presenca.aulas[0].date.split("-").reverse().join("/")} {presenca.aulas[0].start}
                </p>
              )}
              {rank?.disponivel && (
                <div className="mt-5 rounded-2xl border border-gold/30 bg-gold/[0.05] p-4" data-testid="student-rank-card">
                  <div className="flex items-center gap-4">
                    <div className="text-center shrink-0">
                      <p className="font-display text-4xl font-bold text-gold" data-testid="student-rank-posicao">#{rank.posicao}</p>
                      <p className="font-mono text-[9px] tracking-widest text-zinc-500">DE {rank.total_alunos}</p>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-mono text-[10px] tracking-widest text-gold flex items-center gap-1.5"><Trophy size={12} /> RANK · {rank.semestre.toUpperCase()}</p>
                      {rank.posicao > 1 ? (
                        <p className="mt-1 font-mono text-[11px] text-zinc-400" data-testid="student-rank-next">
                          Faltam <span className="text-gold font-bold">{rank.faltam_para_subir}</span> aula(s) para o #{rank.posicao - 1}
                        </p>
                      ) : (
                        <p className="mt-1 font-mono text-[11px] text-gold" data-testid="student-rank-next">Você lidera o ranking da unidade. Oss!</p>
                      )}
                    </div>
                  </div>
                  {rank.ausencia_pendente && (
                    <p className="mt-3 rounded-lg border border-orange-500/50 bg-orange-500/10 px-3 py-2 text-xs text-orange-300" data-testid="student-rank-ausencia">
                      Pendência por ausência frequente: a falta repetida pode custar sua vaga. Se houver justificativa, procure seu instrutor para remover.
                    </p>
                  )}
                </div>
              )}
            </>
          )}
        </motion.section>

        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="bg-[#1C1C23] border border-white/10 rounded-2xl p-8"
          data-testid="student-schedule"
        >
          <p className="font-mono text-xs tracking-[0.3em] text-gold mb-5">MEUS HORÁRIOS</p>
          <div className="space-y-4">
            {CLASS_INFO.map((c) => (
              <div key={c.group} className="flex items-baseline justify-between gap-4 border-b border-white/5 pb-3" data-testid={c.testid}>
                <p className="font-display font-semibold">{c.group}</p>
                <p className="font-mono text-gold">{c.time}</p>
              </div>
            ))}
          </div>
          <p className="font-mono text-xs tracking-widest text-zinc-500 mt-4">SEGUNDA & QUINTA-FEIRA</p>
          <p className="mt-3 flex items-start gap-2 text-sm text-zinc-400" data-testid="student-address">
            <MapPin size={16} className="shrink-0 mt-0.5 text-crimson" />
            R. João Ferreira da Silva, 84 — Além Ponte, Sorocaba/SP
          </p>
        </motion.section>
      </div>

      <motion.section
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.15 }}
        className="mt-6 bg-[#1C1C23] border border-white/10 rounded-2xl p-8"
        data-testid="student-achievements"
      >
        <p className="font-mono text-xs tracking-[0.3em] text-gold mb-5" data-testid="student-achievements-label">
          CONQUISTAS {conquistas ? `(${conquistas.length})` : ""}
        </p>
        <form onSubmit={submitConquista} className="grid sm:grid-cols-[2fr_2fr_1fr_auto] gap-3 mb-6" data-testid="achievement-form">
          <input
            required
            minLength={2}
            value={conqForm.title}
            onChange={(e) => setConqForm((f) => ({ ...f, title: e.target.value }))}
            placeholder="Título (ex: Campeão Paulista)"
            className={inputCls}
            data-testid="achievement-title-input"
          />
          <input
            value={conqForm.competition}
            onChange={(e) => setConqForm((f) => ({ ...f, competition: e.target.value }))}
            placeholder="Competição (opcional)"
            className={inputCls}
            data-testid="achievement-competition-input"
          />
          <input
            type="date"
            value={conqForm.date}
            onChange={(e) => setConqForm((f) => ({ ...f, date: e.target.value }))}
            className={`${inputCls} [color-scheme:dark]`}
            data-testid="achievement-date-input"
          />
          <button
            type="submit"
            disabled={savingConq}
            data-testid="achievement-submit-button"
            className="inline-flex items-center justify-center gap-2 font-display font-semibold text-sm bg-gold text-obsidian px-5 py-3 rounded-xl hover:bg-gold-amber disabled:opacity-60 transition-colors"
          >
            <Plus size={16} /> {savingConq ? "Salvando..." : "Adicionar"}
          </button>
        </form>
        {conquistas === null ? (
          <p className="text-zinc-500 text-sm">Carregando conquistas...</p>
        ) : conquistas.length === 0 ? (
          <div className="flex flex-col items-center gap-3 border border-dashed border-white/15 rounded-2xl py-12 text-zinc-500" data-testid="student-achievements-empty">
            <Trophy size={28} />
            <p className="text-sm">Nenhum título cadastrado ainda. Registre sua primeira conquista acima.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="achievements-table">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left font-mono text-[11px] tracking-widest text-zinc-500 pb-3 pr-4">TÍTULO</th>
                  <th className="text-left font-mono text-[11px] tracking-widest text-zinc-500 pb-3 pr-4">COMPETIÇÃO</th>
                  <th className="text-left font-mono text-[11px] tracking-widest text-zinc-500 pb-3 pr-4">DATA</th>
                  <th className="text-left font-mono text-[11px] tracking-widest text-zinc-500 pb-3 pr-4">STATUS</th>
                  <th className="pb-3" />
                </tr>
              </thead>
              <tbody>
                {conquistas.map((c, i) => (
                  <tr key={c.id} className="border-b border-white/5 last:border-0" data-testid={`achievement-row-${i}`}>
                    <td className="py-3 pr-4 font-display font-semibold">
                      <span className="inline-flex items-center gap-2"><Trophy size={14} className="text-gold shrink-0" />{c.title}</span>
                    </td>
                    <td className="py-3 pr-4 text-zinc-400">{c.competition || "—"}</td>
                    <td className="py-3 pr-4 font-mono text-xs text-zinc-500">
                      {c.date ? c.date.split("-").reverse().join("/") : "—"}
                    </td>
                    <td className="py-3 pr-4">
                      <span
                        className={`font-mono text-[10px] tracking-wider px-2.5 py-1 rounded-full border whitespace-nowrap ${
                          c.status === "validado"
                            ? "border-green-500/50 text-green-400 bg-green-500/10"
                            : "border-yellow-400/50 text-yellow-300 bg-yellow-400/10"
                        }`}
                        data-testid={`achievement-status-${i}`}
                        title={c.status === "validado" && c.validated_by ? `Validado por ${c.validated_by}` : "Aguardando validação do instrutor"}
                      >
                        {c.status === "validado" ? "VALIDADO" : "AGUARDANDO"}
                      </span>
                    </td>
                    <td className="py-3 text-right">
                      <button
                        type="button"
                        onClick={() => removeConquista(c)}
                        data-testid={`achievement-delete-${i}`}
                        className="w-8 h-8 rounded-lg border border-white/15 inline-flex items-center justify-center text-zinc-500 hover:text-crimson hover:border-crimson/60 transition-colors"
                        aria-label={`Remover ${c.title}`}
                      >
                        <Trash2 size={13} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="mt-8 pt-6 border-t border-white/10" data-testid="student-certificates">
          <p className="font-mono text-xs tracking-[0.3em] text-gold mb-4">
            CERTIFICADOS DE GRADUAÇÃO {certificados ? `(${certificados.length})` : ""}
          </p>
          {certificados === null ? (
            <p className="text-zinc-500 text-sm">Carregando certificados...</p>
          ) : certificados.length === 0 ? (
            <p className="text-sm text-zinc-600" data-testid="student-certificates-empty">
              Nenhum certificado emitido ainda. Quando a equipe emitir, ele aparece aqui para download.
            </p>
          ) : (
            <div className="space-y-3">
              {certificados.map((c, i) => (
                <div key={c.id} className="flex items-center gap-4 flex-wrap border border-white/10 rounded-xl px-4 py-3 bg-obsidian" data-testid={`student-certificate-${i}`}>
                  <Award size={20} className="text-gold shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium">Faixa {BELT_LABELS[c.belt]} · {degreeLabel(c.degree)}</p>
                    <p className="font-mono text-[11px] text-zinc-500 mt-0.5">
                      Graduação de {c.graduation_date} · emitido por {c.issued_by}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => downloadCertificado(c)}
                    disabled={downloading === c.id}
                    data-testid={`student-certificate-download-${i}`}
                    className="inline-flex items-center gap-1.5 font-display font-semibold text-xs bg-gold text-obsidian px-4 py-2 rounded-full hover:bg-gold-amber transition-colors disabled:opacity-50 shrink-0"
                  >
                    <Download size={14} /> {downloading === c.id ? "Baixando..." : "Baixar PDF"}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </motion.section>

      <motion.section
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
        className="mt-6 bg-[#1C1C23] border border-white/10 rounded-2xl p-8"
        data-testid="student-history"
      >
        <p className="font-mono text-xs tracking-[0.3em] text-gold mb-5" data-testid="student-history-label">
          MINHA TRAJETÓRIA {historico ? `(${historico.length})` : ""}
        </p>
        {historico === null ? (
          <p className="text-zinc-500 text-sm">Carregando trajetória...</p>
        ) : historico.length === 0 ? (
          <p className="text-sm text-zinc-500" data-testid="student-history-empty">Nenhuma graduação registrada ainda. Cada grau conquistado aparecerá aqui.</p>
        ) : (
          <div className="space-y-4">
            {historico.map((g, i) => (
              <div key={g.id} className="flex items-center gap-4 flex-wrap border-b border-white/5 last:border-0 pb-4 last:pb-0" data-testid={`student-history-item-${i}`}>
                <BeltBar belt={g.belt} degree={g.degree} size="sm" />
                <div>
                  <p className="font-display font-semibold text-sm">
                    Faixa {BELT_LABELS[g.belt]} · {degreeLabel(g.degree)}
                  </p>
                  <p className="font-mono text-[11px] text-zinc-500 mt-0.5">
                    {new Date(g.created_at).toLocaleDateString("pt-BR")} · graduado por {g.granted_by}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </motion.section>

      <section className="mt-12" data-testid="student-announcements">
        <p className="font-mono text-xs tracking-[0.3em] text-gold mb-6" data-testid="student-announcements-label">
          AVISOS DO PROJETO {avisos ? `(${avisos.length})` : ""}
        </p>
        {avisos === null ? (
          <p className="text-zinc-500 text-sm">Carregando avisos...</p>
        ) : avisos.length === 0 ? (
          <div className="flex flex-col items-center gap-4 border border-dashed border-white/15 rounded-2xl py-16 text-zinc-500" data-testid="student-empty-announcements">
            <Megaphone size={32} />
            <p className="text-sm">Nenhum aviso no momento. Oss!</p>
          </div>
        ) : (
          <div className="space-y-4">
            {avisos.map((a, i) => (
              <motion.article
                key={a.id}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05, duration: 0.4 }}
                className="bg-surface border border-white/10 hover:border-gold/30 rounded-2xl p-6 transition-colors"
                data-testid={`student-announcement-${i}`}
              >
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <h2 className="font-display font-semibold text-lg">{a.title}</h2>
                  <span className="font-mono text-xs text-zinc-500">
                    {new Date(a.created_at).toLocaleDateString("pt-BR")}
                  </span>
                </div>
                <p className="text-sm text-zinc-300 mt-2 leading-relaxed whitespace-pre-wrap">{a.message}</p>
              </motion.article>
            ))}
          </div>
        )}
      </section>
    </AppShell>
  );
}
